import React, { useState } from 'react';
import { getLandingPath } from '../../utils/rbac';
import { NAV_ITEMS } from '../../config/navItems';
import { useLocation } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { useDispatch, useSelector } from 'react-redux';
import { login, multiUserLogin, verifyTotpLogin, totpEnrollSetup, totpEnrollVerify } from '../../redux/actions/userActions';
import { validateEmail } from '../../utils/validators';
import { getRememberedEmail, saveRememberedEmail, clearRememberedEmail } from '../../utils/deviceFingerprint';

const useLoginFunctions = history => {
  const { search } = useLocation();
  const dispatch = useDispatch();

  const [token, setToken] = React.useState('');
  const [tokenPassword, setTokenPassword] = React.useState({ value: '', error: '', validated: false });
  const [email, setEmail] = React.useState({ value: '', error: '', validated: false });
  const [password, setPassword] = React.useState({ value: '', error: '', validated: false });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpQR, setTotpQR] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);

  const userLogin = useSelector(state => state.userLogin);
  const isLoading = userLogin && userLogin.loading;

  React.useEffect(() => {
    if (search && search.includes('?token=')) {
      setToken(search.split('?token=')[1]);
    }
  }, [search]);

  React.useEffect(() => {
    const hasLoginSuccess = !search && userLogin && userLogin.userInfo && userLogin.userInfo.status === 'success';
    console.log('[LOGIN-ROUTE]', {
      search,
      hasLoginSuccess,
      status: userLogin?.userInfo?.status,
      path: window.location.hash,
      userData: userLogin?.userInfo?.data?.user ? `User: ${userLogin.userInfo.data.user.role}` : 'NO_DATA',
    });

    if (hasLoginSuccess) {
      const user = userLogin.userInfo.data?.user || userLogin.userInfo.data?.data;
      if (user && user.role) {
        let targetPath = '/dashboard';
        if (user.role === 'admin') {
          // Regional deploys (ch.getregure.com) have no platform surface.
          // Send SaaS God straight to /account-settings so we skip the
          // /platform → guard → /account-settings redirect chain that
          // briefly rendered PlatformOverview. Scope is cached by
          // useDeploymentScope; fall back to /platform when unknown so
          // the platform-admin deploy default is preserved. D-01/D-02.
          const scope = (typeof sessionStorage !== 'undefined'
            && sessionStorage.getItem('regure.deployment.scope')) || 'platform-admin';
          targetPath = scope === 'platform-admin' ? '/platform' : '/account-settings';
        }
        else {
          // Land on the first page this role's own navigation offers, so
          // the landing page and the highlighted nav item are always the
          // same one. Portal roles used to be routed by a hardcoded pair
          // that had drifted out of step with the sidebar.
          targetPath = getLandingPath(user.role, NAV_ITEMS, undefined, '/dashboard');
        }
        console.log('[LOGIN-ROUTE] REDIRECTING TO', targetPath, 'for role', user.role);
        history.push(targetPath);
      } else {
        console.log('[LOGIN-ROUTE] WAITING for user data...');
      }
    } else if (userLogin && userLogin.error) {
      setPassword({ value: '', error: userLogin.error, validated: false });
      setTokenPassword({ value: '', error: userLogin.error, validated: false });
    }
  }, [history, userLogin, search]);

  // Auto-fetch QR code when enrollment is required
  React.useEffect(() => {
    const info = userLogin?.userInfo;
    if (info?.status === 'totp_enrollment_required' && !totpQR && !totpLoading) {
      setTotpLoading(true);
      dispatch(totpEnrollSetup(info.mfaChallenge))
        .then(data => {
          setTotpQR(data.data.qrDataUri);
          setTotpSecret(data.data.secret);
        })
        .catch(() => setTotpError('Failed to start 2FA setup. Please try again.'))
        .finally(() => setTotpLoading(false));
    }
  }, [userLogin, totpQR, totpLoading, dispatch]);

  // Load remembered email on mount
  React.useEffect(() => {
    const rememberedEmail = getRememberedEmail();
    if (rememberedEmail) {
      setEmail({ value: rememberedEmail, error: '', validated: true });
    }
  }, []);

  const onTotpSubmit = async e => {
    if (e && e.preventDefault) e.preventDefault();
    if (totpCode.length !== 6) return;
    setTotpError('');
    setTotpLoading(true);
    try {
      const info = userLogin?.userInfo;
      if (info?.status === 'totp_enrollment_required') {
        await dispatch(totpEnrollVerify(info.mfaChallenge, totpCode));
      } else {
        await dispatch(verifyTotpLogin(info.mfaChallenge, totpCode));
      }
    } catch {
      setTotpError('Invalid code. Please try again.');
    } finally {
      setTotpLoading(false);
    }
  };

  const onLoginHandler = async e => {
    if (e && e.preventDefault) e.preventDefault();
    if (!search) {
      if (!email.value.trim()) {
        setEmail({ ...email, error: 'Please enter your email address', validated: false });
      } else if (!validateEmail(email.value)) {
        setEmail({ ...email, error: 'Please enter a valid email address', validated: false });
      } else if (!password.value.trim()) {
        setPassword({ ...password, error: 'Please enter your password', validated: false });
      } else if (password.value.length < 8) {
        setPassword({ ...password, error: 'Password must have at least 8 characters', validated: false });
      } else {
        const ciphertext = CryptoJS.AES.encrypt(JSON.stringify({ email: email.value, password: password.value }), process.env.REACT_APP_LOGIN_PASSWORD_SALT).toString();
        // Save email to localStorage if "Remember this device" is checked
        if (rememberDevice) {
          saveRememberedEmail(email.value.trim());
        } else {
          clearRememberedEmail();
        }
        dispatch(login(email.value.trim(), password.value, ciphertext, rememberDevice));
      }
    } else {
      if (!tokenPassword.value.trim()) {
        setTokenPassword({ ...tokenPassword, error: 'Please enter your password', validated: false });
      } else if (tokenPassword.value.length < 8) {
        setTokenPassword({ ...tokenPassword, error: 'Password must have at least 8 characters', validated: false });
      } else {
        const newTokenPassword = CryptoJS.AES.encrypt(JSON.stringify({ password: tokenPassword.value }), process.env.REACT_APP_SECURE_KEY).toString();
        const data = await dispatch(multiUserLogin(token, newTokenPassword));
        // multiUserLogin will trigger the same useEffect above when user details are loaded
        // so we don't need to redirect here
      }
    }
  };
  return {
    showPassword,
    setShowPassword,
    rememberDevice,
    setRememberDevice,
    totpCode,
    setTotpCode,
    totpError,
    setTotpError,
    totpLoading,
    setTotpLoading,
    totpQR,
    setTotpQR,
    totpSecret,
    setTotpSecret,
    isLoading,
    onTotpSubmit,
    onLoginHandler,
    userLogin,
    search,
    email,
    setEmail,
    password,
    setPassword,
    tokenPassword,
    setTokenPassword,
  };
};

export default useLoginFunctions;
